import type { LocalRow, SeedTables } from '../types/localDb.types'
import { USER_IDS } from './platform.seed'
import { SEED_GROUP, seedId } from './seedId'
import { claimPalletId } from './wms-claim.seed'
import { FG_WAREHOUSE_ID } from './wms-fg.seed'
import { WF_DSP_FG } from './workflow-dsp.seed'

/**
 * Pemusnahan FG (C12) — DSP-01/01A/02 (ERD v010: `fg_destruction`, `fg_ng_pack`). Angka mengikuti prototype v7.44
 * (`jal-seed.js` Phase 10): lima claim Disetujui menghasilkan lima baris Not Good di ZN-01-P1 — tiga sudah terjadwal di
 * DSP-260818-011 (disetujui Hendra, 2026-08-25 09:00; 199 + 12 + 10 = 221 keping pita), dua menunggu di antrean
 * (CLM-260816-004 24 pack, CLM-260816-005 16 pack).
 *
 * Kolom di luar ERD ([ERD-GAP], laporan 18): `fg_destruction.{plan_note, reschedule_reason, scheduled_by_id, total_pack,
 * approved_at, is_destroyed, destroyed_at}`, `fg_ng_pack.{ng_no, claim_id, queued_at, current_area, destroyed_at,
 * parent_pack_id, note}`, tabel `fg_destruction_task(+_line)` (tugas pemindahan ke ZM-01 per petugas), zona ZM-01.
 */

const utc = (at: string) => new Date(at.replace(' ', 'T') + ':00+07:00').toISOString()
const audit = (userId: string | null, at: string) => ({
  status: 'published',
  sort: null,
  user_created: userId,
  date_created: utc(at),
  user_updated: null,
  date_updated: null,
  user_deleted: null,
  date_deleted: null
})

export const DESTRUCTION_ZONE = ['ZM-01', 'Area Pemusnahan', 'DESTROY_FG'] as const
export const destructionZoneId = () => seedId(SEED_GROUP.warehouseZone, 175)
export const DESTRUCTION_ID = seedId(SEED_GROUP.fgDestruction, 1)

/** [claim, tanggal masuk antrean, dijadwalkan?] — urutan = penomoran NGP-2608-nnn prototype. */
const PACKS: [string, string, boolean][] = [
  ['CLM-260818-003', '2026-08-18 09:40', true],
  ['CLM-260818-002', '2026-08-18 09:40', true],
  ['CLM-260818-001', '2026-08-18 09:40', true],
  ['CLM-260816-004', '2026-08-15 13:20', false],
  ['CLM-260816-005', '2026-08-15 13:20', false]
]

export function applyDestructionData(seed: SeedTables): SeedTables {
  const ulil = USER_IDS.ulil
  const hendra = USER_IDS.hendra
  const claims = new Map((seed.claim ?? []).map(row => [String(row.clm_no), row]))
  const fg_ng_pack: LocalRow[] = []
  PACKS.forEach(([clmNo, queuedAt, scheduled], index) => {
    const claim = claims.get(clmNo)
    const item = (seed.claim_item ?? []).find(row => row.claim_id === claim?.id && Number(row.qty_ng ?? 0) > 0)
    if (!claim || !item) return
    fg_ng_pack.push({
      ...audit(USER_IDS.budi, queuedAt),
      id: seedId(SEED_GROUP.fgNgPack, index + 1),
      ng_no: `NGP-2608-${String(index + 1).padStart(3, '0')}`,
      claim_id: claim.id,
      claim_item_id: item.id,
      pallet_id: claimPalletId('ZN-01-P1'),
      qty_pack: Number(item.qty_ng),
      reason_id: item.reject_reason_id ?? null,
      destruction_id: scheduled ? DESTRUCTION_ID : null,
      ng_status: scheduled ? 'SCHEDULED' : 'IN_QUARANTINE',
      queued_at: utc(queuedAt),
      current_area: 'ZN-01',
      destroyed_at: null,
      parent_pack_id: null,
      note: null
    })
  })
  const scheduledPacks = fg_ng_pack.filter(row => row.destruction_id)
  const total = scheduledPacks.reduce((sum, row) => sum + Number(row.qty_pack), 0)

  const submissionId = seedId(SEED_GROUP.wxlSubmission, 1400)
  const log = (
    n: number,
    at: string,
    userId: string,
    action: Parameters<typeof WF_DSP_FG.actionId>[0],
    actor: Parameters<typeof WF_DSP_FG.actorId>[0]
  ) => {
    const [from, to] = WF_DSP_FG.actionStatus[action]
    return {
      ...audit(userId, at),
      id: seedId(SEED_GROUP.wxlSubmissionLog, 41000 + n),
      workflow_id: WF_DSP_FG.workflowId,
      submission_id: submissionId,
      action_id: WF_DSP_FG.actionId(action),
      actor_id: WF_DSP_FG.actorId(actor),
      from_status_id: WF_DSP_FG.statusId(from),
      to_status_id: WF_DSP_FG.statusId(to),
      approval_total_count: 1,
      approval_approved_count: 1,
      approval_rejected_count: 0,
      reason: null,
      entity: 'fg_destruction',
      entity_id: DESTRUCTION_ID
    }
  }

  const fg_destruction: LocalRow[] = scheduledPacks.length
    ? [
        {
          ...audit(ulil, '2026-08-18 10:11'),
          id: DESTRUCTION_ID,
          dsp_no: 'DSP-260818-011',
          plan_datetime: utc('2026-08-25 09:00'),
          status_id: WF_DSP_FG.statusId('DSP_FG_APPROVED'),
          submission_id: submissionId,
          approver_id: hendra,
          done_at: null,
          plan_note: null,
          reschedule_reason: null,
          scheduled_by_id: ulil,
          total_pack: total,
          approved_at: utc('2026-08-18 10:27'),
          is_destroyed: false,
          destroyed_at: null
        }
      ]
    : []

  return {
    ...seed,
    /* baris Not Good claim v7.50 yang belum disimpan (Menunggu Disimpan) ikut dari seed claim */
    fg_ng_pack: [...fg_ng_pack, ...(seed.fg_ng_pack ?? [])],
    fg_destruction,
    fg_destruction_task: [],
    fg_destruction_task_line: [],
    warehouse_zone: [
      ...(seed.warehouse_zone ?? []),
      {
        ...audit(ulil, '2026-08-01 07:00'),
        id: destructionZoneId(),
        code: DESTRUCTION_ZONE[0],
        name: DESTRUCTION_ZONE[1],
        warehouse_id: FG_WAREHOUSE_ID,
        zone_type: DESTRUCTION_ZONE[2]
      }
    ],
    wxl_submission: [
      ...(seed.wxl_submission ?? []),
      ...(fg_destruction.length
        ? [
            {
              ...audit(ulil, '2026-08-18 10:11'),
              id: submissionId,
              workflow_id: WF_DSP_FG.workflowId,
              entity: 'fg_destruction',
              entity_id: DESTRUCTION_ID,
              source: 'ACTION',
              current_status_id: WF_DSP_FG.statusId('DSP_FG_APPROVED'),
              last_action_id: WF_DSP_FG.actionId('ACT_DSP_FG_APP'),
              workflow_snapshot: null
            }
          ]
        : [])
    ],
    wxl_submission_log: [
      ...(seed.wxl_submission_log ?? []),
      ...(fg_destruction.length
        ? [
            log(1, '2026-08-18 10:11', ulil, 'ACT_DSP_FG_SEND', 'ACT_DSP_FG_ADMIN_FG'),
            log(2, '2026-08-18 10:27', hendra, 'ACT_DSP_FG_APP', 'ACT_DSP_FG_SPV_FG')
          ]
        : [])
    ]
  }
}
